import { useRef, useState, type FormEvent } from "react";

interface NerdChatPillProps {
  disabled?: boolean;
  fullBleed?: boolean;
  /** Nudge right so it doesn't cover the theater collapse control. */
  shiftEnd?: boolean;
  onSend: (text: string) => boolean;
}

/** Floating purple chat input shown when the chat sidebar is collapsed. */
export function NerdChatPill({
  disabled,
  fullBleed,
  shiftEnd,
  onSend,
}: NerdChatPillProps) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    const t = draft.trim();
    if (!t || disabled) return;
    if (onSend(t)) setDraft("");

    // Don't let focus jump to the flashcard face after Send / Enter.
    document
      .querySelectorAll<HTMLElement>(".nerd-flash-face")
      .forEach((el) => el.blur());

    // Enter keyup can land on whatever steals focus after submit (flash face,
    // top-bar video toggle) and activate it — swallow this keyup.
    const blockEnterKeyUp = (ev: KeyboardEvent) => {
      if (ev.key !== "Enter") return;
      ev.preventDefault();
      ev.stopPropagation();
    };
    window.addEventListener("keyup", blockEnterKeyUp, true);
    window.setTimeout(() => {
      window.removeEventListener("keyup", blockEnterKeyUp, true);
    }, 0);

    inputRef.current?.focus({ preventScroll: true });
  }

  return (
    <form
      className={`nerd-chat-pill${fullBleed ? " full" : ""}${shiftEnd ? " shift-end" : ""}`}
      onSubmit={handleSubmit}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Say something…"
        maxLength={200}
        disabled={disabled}
        aria-label="Chat message"
      />
      <button type="submit" disabled={disabled || !draft.trim()}>
        Send
      </button>
    </form>
  );
}
